import { useState } from 'react';
import {
  KeyIcon,
  PlusCircleIcon,
  TrashIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ListBulletIcon,
  CpuChipIcon,
  SparklesIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';

interface APIKey {
  id: string;
  name: string;
  key: string;
  created: string;
  lastUsed: string;
  status: 'active' | 'revoked';
  models: string[];
  permissions: {
    chat: boolean;
    completion: boolean;
    embedding: boolean;
    imageGeneration: boolean;
  };
  limits: {
    tokensPerMinute: number;
    tokensPerDay: number;
    imagesPerMinute: number;
    imagesPerDay: number;
  };
  usage: {
    tokensUsed: number;
    imagesGenerated: number;
    successRate: number;
    averageLatency: number;
  };
}

const mockApiKeys: APIKey[] = [
  {
    id: '1',
    name: 'Production API Key',
    key: 'mk_live_123456789',
    created: '2024-01-15',
    lastUsed: '2024-01-20',
    status: 'active',
    models: ['gpt-4', 'gpt-3.5-turbo', 'claude-2', 'dall-e-3'],
    permissions: {
      chat: true,
      completion: true,
      embedding: true,
      imageGeneration: true,
    },
    limits: {
      tokensPerMinute: 10000,
      tokensPerDay: 1000000,
      imagesPerMinute: 10,
      imagesPerDay: 1000,
    },
    usage: {
      tokensUsed: 750000,
      imagesGenerated: 500,
      successRate: 99.8,
      averageLatency: 450,
    },
  },
  {
    id: '2',
    name: 'Development API Key',
    key: 'mk_test_987654321',
    created: '2024-01-10',
    lastUsed: '2024-01-19',
    status: 'active',
    models: ['gpt-3.5-turbo', 'dall-e-2'],
    permissions: {
      chat: true,
      completion: true,
      embedding: false,
      imageGeneration: true,
    },
    limits: {
      tokensPerMinute: 5000,
      tokensPerDay: 100000,
      imagesPerMinute: 5,
      imagesPerDay: 500,
    },
    usage: {
      tokensUsed: 45000,
      imagesGenerated: 347,
      successRate: 98.5,
      averageLatency: 380,
    },
  },
];

export default function APIKeys() {
  const [viewMode, setViewMode] = useState<'list' | 'chart'>('list');
  const [apiKeys, setApiKeys] = useState<APIKey[]>(mockApiKeys);

  const generateNewKey = () => {
    const newKey: APIKey = {
      id: String(Date.now()),
      name: 'New API Key',
      key: `mk_${Math.random().toString(36).substring(2)}`,
      created: new Date().toISOString().split('T')[0],
      lastUsed: '-',
      status: 'active',
      models: ['gpt-3.5-turbo', 'dall-e-2'],
      permissions: {
        chat: true,
        completion: true,
        embedding: false,
        imageGeneration: true,
      },
      limits: {
        tokensPerMinute: 5000,
        tokensPerDay: 100000,
        imagesPerMinute: 5,
        imagesPerDay: 500,
      },
      usage: {
        tokensUsed: 0,
        imagesGenerated: 0,
        successRate: 0,
        averageLatency: 0,
      },
    };
    setApiKeys([...apiKeys, newKey]);
  };

  const revokeKey = (id: string) => {
    setApiKeys(
      apiKeys.map((key) =>
        key.id === id ? { ...key, status: 'revoked' as const } : key
      )
    );
  };

  const recycleKey = (id: string) => {
    setApiKeys(
      apiKeys.map((key) =>
        key.id === id
          ? {
              ...key,
              key: `mk_${Math.random().toString(36).substring(2)}`,
              created: new Date().toISOString().split('T')[0],
              lastUsed: '-',
              usage: { tokensUsed: 0, imagesGenerated: 0, successRate: 0, averageLatency: 0 },
            }
          : key
      )
    );
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <KeyIcon className="h-6 w-6 text-white/90" />
            <h1 className="text-xl font-medium text-white">API Keys</h1>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex bg-white/[0.05] rounded-lg p-1">
              <button
                className={`px-3 py-1 rounded ${
                  viewMode === 'list'
                    ? 'bg-white/10 text-white'
                    : 'text-white/50 hover:text-white/80'
                }`}
                onClick={() => setViewMode('list')}
              >
                <ListBulletIcon className="h-5 w-5" />
              </button>
              <button
                className={`px-3 py-1 rounded ${
                  viewMode === 'chart'
                    ? 'bg-white/10 text-white'
                    : 'text-white/50 hover:text-white/80'
                }`}
                onClick={() => setViewMode('chart')}
              >
                <ChartBarIcon className="h-5 w-5" />
              </button>
            </div>
            <button
              onClick={generateNewKey}
              className="btn-primary flex items-center space-x-2"
            >
              <PlusCircleIcon className="h-5 w-5" />
              <span>Generate New Key</span>
            </button>
          </div>
        </div>

        {/* List View */}
        {viewMode === 'list' && (
          <div className="divide-y divide-white/[0.05]">
            {apiKeys.map((apiKey) => (
              <div
                key={apiKey.id}
                className="px-8 py-6 hover:bg-white/[0.02] transition-colors duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <h3 className="text-white font-medium">{apiKey.name}</h3>
                    <div className="flex items-center space-x-2">
                      <code className="text-sm bg-white/[0.05] px-2 py-1 rounded text-white/70">
                        {apiKey.key}
                      </code>
                      <span
                        className={`text-sm px-2 py-1 rounded-full ${
                          apiKey.status === 'active'
                            ? 'bg-emerald-500/10 text-emerald-300'
                            : 'bg-rose-500/10 text-rose-300'
                        }`}
                      >
                        {apiKey.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-6">
                    <div className="text-right">
                      <div className="text-sm text-white/50">Tokens Used</div>
                      <div className="text-white font-medium">
                        {formatNumber(apiKey.usage.tokensUsed)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-white/50">Images Generated</div>
                      <div className="text-white font-medium">
                        {formatNumber(apiKey.usage.imagesGenerated)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-white/50">Success Rate</div>
                      <div className="text-white font-medium">
                        {apiKey.usage.successRate}%
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-white/50">Avg Latency</div>
                      <div className="text-white font-medium">
                        {apiKey.usage.averageLatency}ms
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => recycleKey(apiKey.id)}
                        className="p-2 text-white/50 hover:text-white/90 hover:bg-white/[0.05] rounded-lg transition-colors duration-200"
                        title="Recycle Key"
                      >
                        <ArrowPathIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => revokeKey(apiKey.id)}
                        className="p-2 text-white/50 hover:text-rose-500 hover:bg-white/[0.05] rounded-lg transition-colors duration-200"
                        title="Revoke Key"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="glass-panel-dark p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <CpuChipIcon className="h-5 w-5 text-white/50" />
                      <h4 className="text-sm font-medium text-white">Models</h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {apiKey.models.map((model) => (
                        <span
                          key={model}
                          className="text-xs px-2 py-1 rounded-full bg-white/[0.05] text-white/70"
                        >
                          {model}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="glass-panel-dark p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <SparklesIcon className="h-5 w-5 text-white/50" />
                      <h4 className="text-sm font-medium text-white">Endpoints</h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(apiKey.permissions).map(([key, value]) => (
                        <span
                          key={key}
                          className={`text-xs px-2 py-1 rounded-full ${
                            value
                              ? 'bg-emerald-500/10 text-emerald-300'
                              : 'bg-white/[0.05] text-white/30'
                          }`}
                        >
                          {key === 'imageGeneration' ? 'images' : key}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="glass-panel-dark p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <PhotoIcon className="h-5 w-5 text-white/50" />
                      <h4 className="text-sm font-medium text-white">Limits</h4>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-white/50">Tokens/min:</span>
                        <span className="text-white">
                          {formatNumber(apiKey.limits.tokensPerMinute)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">Tokens/day:</span>
                        <span className="text-white">
                          {formatNumber(apiKey.limits.tokensPerDay)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">Images/min:</span>
                        <span className="text-white">
                          {formatNumber(apiKey.limits.imagesPerMinute)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">Images/day:</span>
                        <span className="text-white">
                          {formatNumber(apiKey.limits.imagesPerDay)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-4 text-sm text-white/50">
                  <div>Created: {apiKey.created}</div>
                  <div>Last Used: {apiKey.lastUsed}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chart View */}
        {viewMode === 'chart' && (
          <div className="p-8">
            <div className="text-white/50 text-center">
              Chart view will display token and image usage metrics over time
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
